/**
 * Registry of CMS text fields that are wired for the inline content editor
 * (AHD-7396). This is the **source of truth** for "what is inline-editable",
 * and the counterpart to the next-intl mapping ratchet
 * (`__tests__/mapping-audit.test.ts`).
 *
 * Why a registry, not just a scan? A scan of `editAttrs(...)` call sites can
 * tell you what *is* stamped, but it can't tell you what *should* be — so a
 * stamp that gets deleted or renamed during a refactor disappears silently and
 * the field just stops being editable, with no error. Pinning the intended set
 * here lets `scripts/lint-cms-edit-stamps.ts` (gated in CI via
 * `__tests__/cms-edit-stamps.test.ts`) fail when:
 *   - a registered field has no live `editAttrs({ collection, field })` call
 *     under `app/` or `components/` (a stamp was dropped), or
 *   - an `editAttrs(...)` call references a (collection, field) that isn't
 *     registered here (drift, or a typo in the collection/field name).
 *
 * Adding a new inline-editable CMS field is a deliberate one-line act:
 *   1. spread `editAttrs({ collection, id, field, locale, enabled })` onto the
 *      element that renders the value (see `components/ui/testimonial-card.tsx`
 *      + `app/.../testimonials-section.tsx` for the reference wiring), then
 *   2. add the `{ collection, field }` pair below.
 */

export interface EditableCmsField {
  /** Payload collection slug, e.g. `"testimonials"`. */
  collection: string
  /** Field name within the doc, e.g. `"quote"`. */
  field: string
  /** Field shape — drives the injector and the editor modal. */
  kind: "scalar" | "html" | "lexical"
  /** Short note on where/why it's editable (kept for humans; not asserted). */
  note?: string
}

/**
 * The intended inline-editable CMS text fields. Keep in sync with the
 * `editAttrs(...)` call sites — the lint enforces the bidirectional match.
 */
export const EDITABLE_CMS_FIELDS: readonly EditableCmsField[] = [
  {
    collection: "testimonials",
    field: "quote",
    kind: "scalar",
    note: "review body on the testimonials carousel (testimonial-card.tsx) + instagram caption",
  },
  {
    collection: "testimonials",
    field: "headline",
    kind: "scalar",
    note: "testimonial card title (testimonial-card.tsx)",
  },
  {
    collection: "testimonials",
    field: "authorName",
    kind: "scalar",
    note: "testimonial author name (testimonial-card.tsx + video-testimonial.tsx)",
  },
  {
    collection: "testimonials",
    field: "authorTitle",
    kind: "scalar",
    note: "video-testimonial author subtitle",
  },
  {
    collection: "testimonials",
    field: "instagramHandle",
    kind: "scalar",
    note: "instagram-testimonial handle",
  },
  {
    collection: "testimonials",
    field: "instagramFollowers",
    kind: "scalar",
    note: "instagram-testimonial follower count",
  },

  // FAQ
  {
    collection: "faq",
    field: "question",
    kind: "scalar",
    note: "FAQ accordion question",
  },
  {
    collection: "faq",
    field: "answer",
    kind: "html",
    note: "FAQ accordion answer (sanitized HTML string)",
  },
  {
    collection: "faq-categories",
    field: "name",
    kind: "scalar",
    note: "FAQ category label/heading",
  },
  {
    collection: "faq-categories",
    field: "description",
    kind: "scalar",
    note: "FAQ category hero description",
  },

  // Content taxonomy (biomarker/disease explorer)
  {
    collection: "categories",
    field: "name",
    kind: "scalar",
    note: "category section heading",
  },
  {
    collection: "categories",
    field: "description",
    kind: "scalar",
    note: "category info tooltip",
  },

  // Biomarkers / diseases / symptoms — clinical content (see /medical-content-validation)
  {
    collection: "biomarkers",
    field: "name",
    kind: "scalar",
    note: "biomarker title/heading (localized text field; detail h1)",
  },
  {
    collection: "biomarkers",
    field: "description",
    kind: "scalar",
    note: "biomarker detail body + explorer panel",
  },
  {
    collection: "biomarkers",
    field: "summary",
    kind: "scalar",
    note: "biomarker detail summary paragraph (only stamped when the CMS summary field is set, not the derived first-sentence)",
  },
  {
    collection: "biomarkers",
    field: "testingFrequencyAnswer",
    kind: "scalar",
    note: "biomarker testing-frequency prose",
  },
  {
    collection: "biomarkers",
    field: "testingFrequencyBaseline",
    kind: "scalar",
    note: "biomarker testing-frequency baseline",
  },
  {
    collection: "biomarkers",
    field: "testingFrequencyMonitoring",
    kind: "scalar",
    note: "biomarker testing-frequency monitoring",
  },
  {
    collection: "biomarkers",
    field: "testingFrequencyNote",
    kind: "scalar",
    note: "biomarker testing-frequency note",
  },
  {
    collection: "diseases",
    field: "name",
    kind: "scalar",
    note: "disease title/heading (localized text field; detail h1)",
  },
  {
    collection: "diseases",
    field: "description",
    kind: "scalar",
    note: "disease detail body + explorer panel",
  },
  {
    collection: "symptoms",
    field: "description",
    kind: "scalar",
    note: "symptom explorer row + detail header",
  },

  // Authors
  {
    collection: "authors",
    field: "name",
    kind: "scalar",
    note: "author name (detail header + article byline)",
  },
  { collection: "authors", field: "role", kind: "scalar", note: "author role" },
  { collection: "authors", field: "bio", kind: "scalar", note: "author bio" },

  // Careers
  {
    collection: "job-listings",
    field: "title",
    kind: "scalar",
    note: "job title (listing card + detail)",
  },

  // Locations
  {
    collection: "locations",
    field: "description",
    kind: "scalar",
    note: "clinic/location prose intro",
  },
  {
    collection: "locations",
    field: "languageNote",
    kind: "scalar",
    note: "clinic language note",
  },
  {
    collection: "locations",
    field: "publicTransport",
    kind: "scalar",
    note: "clinic public-transport directions",
  },

  // Insights articles — title + Lexical rich-text body
  {
    collection: "blog-posts",
    field: "title",
    kind: "scalar",
    note: "insights article title (detail h1)",
  },
  {
    collection: "blog-posts",
    field: "content",
    kind: "lexical",
    note: "insights article body",
  },
]

/**
 * Is `(collection, field)` an inline-editable CMS text field? The editor's CMS
 * read/write API routes use this to bound which docs they'll touch with the
 * privileged `CMS_WRITE_TOKEN` — without it, `collection`/`field` come straight
 * off a query param and any internal caller could read (or draft-write) any
 * collection, including Payload's `users`. Restricting to the registry keeps the
 * token's blast radius to the fields the editor actually renders.
 */
export function isEditableCmsField(collection: string, field: string): boolean {
  return EDITABLE_CMS_FIELDS.some(
    (f) => f.collection === collection && f.field === field
  )
}

/**
 * A human-readable label for a (collection, field) — the editor modals lead with
 * this instead of the `collection:id:field` id so a non-technical editor knows
 * what they're editing (AHD-7515 UX polish, finding 5). Prefers the registry
 * `note`; falls back to a humanized field name (`testingFrequencyAnswer` →
 * "testing frequency answer") when a field has no note.
 */
export function cmsFieldLabel(collection: string, field: string): string {
  const entry = EDITABLE_CMS_FIELDS.find(
    (f) => f.collection === collection && f.field === field
  )
  if (entry?.note) {
    // The note is a "where/why" sentence; take its leading clause (before the
    // first parenthetical or em-dash) as the headline, sentence-cased.
    const head = entry.note.split(/\s*[(—]/)[0].trim()
    return head || humanizeField(field)
  }
  return humanizeField(field)
}

/** `testingFrequencyAnswer` → "testing frequency answer"; `authorName` → "author
 *  name". camelCase / kebab-case → spaced, lower-cased. */
function humanizeField(field: string): string {
  return field
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim()
}
